import { useEffect } from "react";
import { createPortal } from "react-dom";
import type { Vector } from "@shared/types";

// Leadership briefing on what an "AI-ready workforce" means. The five sections
// map 1:1 to the readiness radar vectors, so each one is colour-coded to its
// vector and tagged with the same short label shown on the radar.
type Stat = { value: string; label: string };
type Group = { label: string; items: string[] };
type Section = {
  index: number; // matches Vector.index → colour + short label
  title: string;
  stats: Stat[];
  note?: string;
  group?: Group;
};

const SOURCE_URL =
  "https://sites.ey.com/sites/DS_Consulting/PC/Pages/ey-work-reimagined.aspx";

const SECTIONS: Section[] = [
  {
    index: 1,
    title: "AI's role is clearly defined and commonly understood",
    stats: [
      { value: "25%", label: "of AI adoption success is mindset" },
      { value: "62%", label: "have formal goals or incentives to adopt AI" },
      { value: "44%", label: "are involved in their org's AI programs" },
    ],
    note: "Employers with Talent Advantage have 63% reporting that culture is significantly better than 12 months ago vs. 22% in the Talent Middle and 3% in the Disadvantage group.",
  },
  {
    index: 2,
    title: "The right AI and workflow tools are available and used appropriately",
    stats: [
      { value: "5%", label: "of employees are advanced AI users" },
      { value: "26%", label: "of AI adoption success is toolset" },
      { value: "23–58%", label: "bring their own personal AI tools (by sector)" },
    ],
    note: "Toolset represents 26% of AI adoption success, seeing that despite 61% of employees agreeing that AI tools are tailored to their role, there is large sector variation of employees bringing their own personal AI tools to work (23% - 58%), with some even paying out of pocket for their own subscriptions.",
    group: {
      label: "Investments that would lift productivity",
      items: ["Leading AI tools 49%", "Knowledge management 47%", "Hardware 36%"],
    },
  },
  {
    index: 3,
    title: "Organizational structures and processes align to how AI is leveraged",
    stats: [
      { value: "18%", label: "believe their organization is innovative" },
      { value: "19%", label: "believe their organization is efficient" },
      { value: "71%", label: "report consistent, structured change management" },
    ],
    note: "Only 18% of employees believe their organization is innovative and 19% believe they are efficient. 71% of employees believe that their organization has a consistent and structured approach to change management.",
  },
  {
    index: 4,
    title: "Employees have the right skills — and can build the skills they'll need next",
    stats: [
      { value: "49%", label: "of AI adoption success is skillset" },
      { value: "81+ hrs", label: "of AI learning needed for superior outcomes" },
      { value: "45%", label: "intend to leave — near Great Resignation peak" },
    ],
    note: "Given the higher intent to quit, important to evaluate key elements of talent health, which includes highly rated training and development programs (68% above average or excellent), the opportunity to move internally (58%) and that your total rewards package are designed to meet individual and team performance (64%).",
    group: {
      label: "Key talent-health drivers",
      items: ["Learning & development 68%", "Internal mobility 58%", "Rewards alignment 64%"],
    },
  },
  {
    index: 5,
    title: "AI investments contribute transformational, not incremental, value",
    stats: [
      { value: "88%", label: "use AI at work, but only 28% achieve transformation" },
      { value: "17×", label: "more likely to outperform (strongest orgs)" },
    ],
    note: "Organizations with more strategic people and tech capabilities are more likely to agree that their organization outperforms 17x in current economic conditions.",
    group: {
      label: "Five capabilities that work in concert",
      items: [
        "Recruiting & retaining talent",
        "Scaled AI adoption",
        "Continuous learning",
        "Culture & norms shift",
        "Rewards aligned to behavior",
      ],
    },
  },
];

export function Briefing({ vectors, onClose }: { vectors: Vector[]; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const colorFor = (i: number) => vectors.find((v) => v.index === i)?.color ?? "var(--violet)";
  const labelFor = (i: number) => vectors.find((v) => v.index === i)?.shortLabel ?? "";

  return createPortal(
    <div className="briefing-overlay" onClick={onClose}>
      <div className="briefing-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="AI-ready workforce briefing">
        <button className="briefing-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>

        <div className="briefing-head">
          <h2 className="briefing-title">What does it mean to have an AI-ready workforce?</h2>
          <p className="briefing-intro">
            A quick read on what a company needs to be ready — and why it matters. The five themes below
            map to the readiness radar.
          </p>
          <a className="briefing-source" href={SOURCE_URL} target="_blank" rel="noreferrer">
            Source: 2025 EY Work Reimagined Survey →
          </a>
        </div>

        <div className="briefing-sections">
          {SECTIONS.map((s) => {
            const color = colorFor(s.index);
            return (
              <section className="briefing-section" key={s.index} style={{ ["--accent" as string]: color }}>
                <div className="bs-head">
                  <span className="bs-num" style={{ background: color }}>{s.index}</span>
                  <div className="bs-headtext">
                    <span className="bs-tag" style={{ color }}>{labelFor(s.index)}</span>
                    <h3 className="bs-title">{s.title}</h3>
                  </div>
                </div>

                <div className="bs-stats">
                  {s.stats.map((st, i) => (
                    <div className="bs-stat" key={i}>
                      <span className="bs-stat-value" style={{ color }}>{st.value}</span>
                      <span className="bs-stat-label">{st.label}</span>
                    </div>
                  ))}
                </div>

                {s.note && <p className="bs-note">{s.note}</p>}

                {s.group && (
                  <div className="bs-group">
                    <span className="bs-group-label">{s.group.label}</span>
                    <div className="bs-chips">
                      {s.group.items.map((it) => (
                        <span className="bs-chip" key={it}>{it}</span>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}
